import { computed, type Ref } from 'vue'
import {
  tradingErrorCopy,
  useDebouncedValue,
  useSellQuoteQuery,
  type SellQuote,
  type SellQuoteRequest,
} from '../../trading'
import type { SellMarketFigures } from './useSellListingDraft'

/** Prices match to the fil — the server echoes the two-decimal price it was sent. */
const PRICE_TOLERANCE = 0.005

interface SellQuoteSources {
  propertyId: Ref<string>
  tokens: Ref<number>
  price: Ref<number | null>
  figures: Ref<SellMarketFigures>
}

// Server-priced proceeds for the draft listing (POST sell-quote), requested
// once the quantity + price settle. `quote` is only the one for exactly the
// draft on screen; `quoteError` is the first rule the server reports, in spec
// copy with the band / sellable figures filled in.
export function useSellListingQuote({ propertyId, tokens, price, figures }: SellQuoteSources) {
  const request = computed<SellQuoteRequest | null>(() =>
    tokens.value > 0 && price.value !== null
      ? { tokens: tokens.value, pricePerToken: price.value }
      : null,
  )
  const settled = useDebouncedValue(() => request.value)
  const quoteQuery = useSellQuoteQuery(propertyId, settled)

  const quote = computed<SellQuote | null>(() => {
    const server = quoteQuery.data.value
    const draft = request.value
    if (!server || !draft || quoteQuery.isPlaceholderData.value) return null
    const isSameDraft =
      server.tokens === draft.tokens &&
      Math.abs(server.pricePerToken - draft.pricePerToken) < PRICE_TOLERANCE
    return isSameDraft ? server : null
  })

  const isQuoting = computed(() => request.value !== null && quote.value === null)

  const quoteError = computed<string | null>(() => {
    const values = {
      sellable: figures.value.available,
      bandFloor: figures.value.bandMin,
      bandCeiling: figures.value.bandMax,
    }
    for (const code of quote.value?.errors ?? []) {
      const copy = tradingErrorCopy(code, values)
      if (copy) return copy
    }
    return null
  })

  return { quote, isQuoting, quoteError }
}
